import type * as React from 'react';

import { CheckCircleIcon, FlagIcon } from '@phosphor-icons/react';
import { css, html } from 'react-strict-dom';
import { Api } from 'src/api';
import { DateWithTooltip, RouterLink, Text } from 'src/components';
import { Color, Style } from 'src/constants';
import { color } from 'src/constants/color.css';

/**
 * Every dashboard list returns tickets in this same shape.
 */
export type DashboardTicket = Api.Ticket.V_AvailableList;

type Props = {
  ticket: DashboardTicket;
  isCompleted?: boolean;
  /** Shown at the top right, e.g. skill badges or a "Take it" link. */
  action?: React.ReactNode;
  footer?: React.ReactNode;
};

export default function TicketRow({
  ticket,
  isCompleted,
  action,
  footer,
}: Props) {
  const isHighPriority = ticket.priority === 'high';

  return (
    <html.div
      style={[
        styles.row,
        isCompleted && styles.rowCompleted,
        !isCompleted && isHighPriority && styles.rowHighPriority,
      ]}
    >
      <html.div style={[Style.row, Style.alignCenter, styles.metaLine]}>
        <img
          src={`/img/companies/${ticket.company.slug}.png`}
          alt={ticket.company.name}
          style={{ width: 20, height: 20, borderRadius: 4, objectFit: 'cover' }}
        />
        <Text typeface="body.small.medium" color="neutral80">
          #{ticket.number}
        </Text>
        {isHighPriority && (
          <>
            <html.span style={styles.divider} />
            <html.span style={[Style.row, Style.alignCenter, styles.priority]}>
              <FlagIcon size={14} weight="fill" color={Color.red} />
              <Text typeface="body.small.medium" color="red">
                High
              </Text>
            </html.span>
          </>
        )}
        <html.span style={Style.fill} />
        {action}
      </html.div>

      <html.div style={[Style.row, Style.alignStart, styles.titleLine]}>
        {isCompleted && (
          <html.span style={styles.check}>
            <CheckCircleIcon size={18} color={Color.blue} />
          </html.span>
        )}
        <RouterLink
          to={`/company/${ticket.company.slug}/tickets/${ticket.number}`}
          style={{ flex: 1, minWidth: 0 }}
        >
          <Text
            typeface="body.regular"
            color="neutral100"
            lineThrough={isCompleted}
          >
            {ticket.title}
          </Text>
        </RouterLink>
        <html.span style={styles.time}>
          <Text typeface="body.small.regular" color="neutral60">
            <DateWithTooltip timestamp={ticket.createdAt} />
          </Text>
        </html.span>
      </html.div>

      {footer && <html.div style={styles.footer}>{footer}</html.div>}
    </html.div>
  );
}

const styles = css.create({
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    paddingBlock: 12,
    paddingInline: 16,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: color.neutral20,
    ':last-child': {
      borderBottomWidth: 0,
    },
  },
  rowCompleted: {
    backgroundColor: color.blueBg,
  },
  rowHighPriority: {
    backgroundColor: color.accentBg,
  },
  metaLine: {
    gap: 8,
  },
  divider: {
    width: 1,
    height: 14,
    backgroundColor: color.neutral20,
  },
  priority: {
    gap: 4,
  },
  titleLine: {
    gap: 8,
  },
  check: {
    display: 'flex',
    paddingTop: 2,
  },
  time: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  footer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
});
